/**
 * SuperAdminPreviewSoak — Phase 4 follow-up.
 *
 * Single-page go/no-go report for the v1.0.25 preview soak before
 * promoting the C# agent build to production via Releases. Aggregates
 * 7 days (configurable) of capture / apply / verification metrics across
 * ALL tenants. SuperAdmin only.
 */
import React, { useEffect, useState, useCallback } from "react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
    Loader2, RefreshCw, Download, CheckCircle2, XCircle, ShieldAlert,
    AlertTriangle, ShieldCheck, Activity, Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const GATE_LABEL = {
    captures_completed: "Captures completed",
    apply_plans_generated: "Apply plans generated",
    applies_completed: "Applies completed",
    verification_reports_generated: "Verification reports generated",
    no_unexpected_failed_installs: "Failed installs within tolerance (<10 %)",
    low_unknown_results: "Unknown verifications under threshold (<15 %)",
    sensitive_apps_protected: "Sensitive / skipped apps protected",
    tenant_isolation_intact: "Tenant isolation intact",
};

const VERIFY_TONE = {
    verified:         "bg-emerald-900/40 text-emerald-200 border-emerald-700/50",
    version_mismatch: "bg-amber-900/40 text-amber-200 border-amber-700/50",
    name_mismatch:    "bg-amber-900/40 text-amber-200 border-amber-700/50",
    failed_install:   "bg-rose-900/40 text-rose-200 border-rose-700/50",
    manual_required:  "bg-slate-800/60 text-slate-200 border-slate-600/50",
    vendor_skipped:   "bg-zinc-800/60 text-zinc-200 border-zinc-600/50",
    not_attempted:    "bg-zinc-900/40 text-zinc-400 border-zinc-700/50",
    unknown:          "bg-zinc-900/40 text-zinc-400 border-zinc-700/50",
};

const VERIFY_LABEL = {
    verified: "Verified",
    version_mismatch: "Version mismatch",
    name_mismatch: "Name mismatch",
    failed_install: "Failed install",
    manual_required: "Manual required",
    vendor_skipped: "Vendor reinstall",
    not_attempted: "Not attempted",
    unknown: "Unknown",
};

export default function SuperAdminPreviewSoak() {
    const [days, setDays] = useState("7");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await http.get(`/superadmin/preview-soak?days=${days}`);
            setData(r.data);
        } catch (e) {
            toast.error("Soak report failed: " + errMsg(e));
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = async () => {
        try {
            const r = await http.get(`/superadmin/preview-soak.csv?days=${days}`, { responseType: "blob" });
            const cd = r.headers?.["content-disposition"] || "";
            const m = cd.match(/filename="([^"]+)"/);
            const filename = m ? m[1] : `preview-soak.csv`;
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success("Soak report exported");
        } catch (e) {
            toast.error("Export failed: " + errMsg(e));
        }
    };

    return (
        <div className="space-y-4 p-4" data-testid="sa-preview-soak-page">
            <header className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold text-zinc-100">Preview Soak Summary</h1>
                    <p className="text-xs text-zinc-500">
                        Go / No-Go evidence for promoting v1.0.25 to production.
                        Aggregated across all tenants.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={days} onValueChange={setDays}>
                        <SelectTrigger className="h-9 w-[140px]" data-testid="soak-days-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {["1","3","7","14","30","60","90"].map(d => (
                                <SelectItem key={d} value={d}>Last {d} days</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="soak-refresh">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" onClick={exportCsv} data-testid="soak-export">
                        <Download className="w-4 h-4 mr-1.5" /> Export CSV
                    </Button>
                </div>
            </header>

            {!data ? (
                <div className="p-12 text-center text-sm text-zinc-500">
                    {loading ? <><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</> : "No data yet."}
                </div>
            ) : (
                <>
                    {/* Go / No-Go header */}
                    <GoNoGo gates={data.gates} />

                    {/* Funnel cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="soak-funnel">
                        <Metric label="Captures" value={data.captures.total} sub={`${data.captures.completed} completed · ${data.captures.failed} failed`} icon={<Package className="w-4 h-4" />} />
                        <Metric label="Apply Plans" value={data.captures.with_apps_json} sub="captures with apps.json" icon={<Activity className="w-4 h-4" />} />
                        <Metric label="Applies" value={data.applies.total} sub={`${data.applies.completed} completed · ${data.applies.failed} failed`} icon={<Activity className="w-4 h-4" />} />
                        <Metric label="Verifications" value={data.applies.with_verification} sub="applies with diff report" icon={<ShieldCheck className="w-4 h-4" />} />
                    </div>

                    {/* Verification taxonomy */}
                    <section data-testid="soak-verification">
                        <h2 className="text-sm font-semibold text-zinc-200 mb-2">Verification taxonomy (sum across all completed applies)</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-1.5">
                            {Object.keys(VERIFY_LABEL).map(k => (
                                <div key={k}
                                    className={`rounded-md border p-2 text-center ${VERIFY_TONE[k]}`}
                                    data-testid={`soak-verify-${k}`}>
                                    <div className="text-[9px] uppercase tracking-wide opacity-80">{VERIFY_LABEL[k]}</div>
                                    <div className="text-base font-semibold">{data.verification_counts[k] || 0}</div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Sensitive auto-installs (the most important safety table) */}
                    <section data-testid="soak-sensitive">
                        <h2 className="text-sm font-semibold text-zinc-200 mb-2 flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4 text-amber-400" />
                            Sensitive / skipped apps with custom auto-install ({data.sensitive_auto_installs.length})
                        </h2>
                        {data.sensitive_auto_installs.length === 0 ? (
                            <div className="rounded-md border border-emerald-800/40 bg-emerald-950/20 p-3 text-xs text-emerald-200">
                                ✓ No sensitive-class apps were auto-installed during this window. Hard safety rule held.
                            </div>
                        ) : (
                            <div className="rounded-md border border-amber-800/40 bg-amber-950/10 overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead className="bg-amber-950/30 text-amber-200">
                                        <tr>
                                            <th className="px-2 py-1.5 text-left">Tenant</th>
                                            <th className="px-2 py-1.5 text-left">App</th>
                                            <th className="px-2 py-1.5 text-left">Publisher</th>
                                            <th className="px-2 py-1.5 text-left">Operator</th>
                                            <th className="px-2 py-1.5 text-left">When</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.sensitive_auto_installs.map((s, i) => (
                                            <tr key={i} className="border-t border-amber-800/30">
                                                <td className="px-2 py-1.5 font-mono text-zinc-400">{(s.tenant_id || "").slice(0,8)}…</td>
                                                <td className="px-2 py-1.5 text-zinc-200">{s.name}</td>
                                                <td className="px-2 py-1.5 text-zinc-400">{s.publisher}</td>
                                                <td className="px-2 py-1.5 text-zinc-300">{s.set_by || <span className="text-rose-400">unattributed</span>}</td>
                                                <td className="px-2 py-1.5 text-zinc-500">{s.set_at}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    {/* Operator override transitions */}
                    <section data-testid="soak-overrides">
                        <h2 className="text-sm font-semibold text-zinc-200 mb-2">Operator reclassification trends</h2>
                        <div className="rounded-md border border-zinc-800 p-3 text-xs text-zinc-300">
                            <div className="mb-1.5"><span className="text-zinc-500">Total overrides saved:</span> {data.operator_overrides.total}</div>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(data.operator_overrides.transitions || {}).map(([k, v]) => (
                                    <span key={k} className="rounded-md border border-zinc-700 bg-zinc-900/50 px-2 py-0.5">
                                        {k}: <span className="font-semibold text-zinc-100">{v}</span>
                                    </span>
                                ))}
                                {Object.keys(data.operator_overrides.transitions || {}).length === 0 && (
                                    <span className="text-zinc-500">No operator overrides in this window.</span>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* Carry overrides + tenant isolation */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <Metric
                            data-testid="soak-carry-overrides"
                            label="Carry overrides forward"
                            value={data.captures.with_carry_overrides}
                            sub={`captures opted in · ${data.captures.overrides_cloned_total} overrides cloned`}
                            icon={<RefreshCw className="w-4 h-4" />}
                        />
                        <Metric
                            data-testid="soak-tenant-isolation"
                            label="Cross-tenant access denials"
                            value={data.tenant_isolation.cross_tenant_audit_hits}
                            sub={data.tenant_isolation.cross_tenant_audit_hits === 0 ? "✓ no probing detected" : "⚠ investigate audit log"}
                            tone={data.tenant_isolation.cross_tenant_audit_hits === 0 ? "ok" : "warn"}
                            icon={<ShieldCheck className="w-4 h-4" />}
                        />
                    </div>

                    <footer className="text-[11px] text-zinc-500 pt-2 border-t border-zinc-800">
                        Generated at {data.generated_at} · scope {data.scope} · window {data.window_days} days
                    </footer>
                </>
            )}
        </div>
    );
}

function GoNoGo({ gates }) {
    const overall = gates?.overall_go;
    const gateEntries = Object.entries(gates || {}).filter(([k]) => k !== "overall_go");
    return (
        <section data-testid="soak-go-nogo"
            className={`rounded-lg border p-4 ${overall ? "border-emerald-700/50 bg-emerald-950/20" : "border-amber-700/50 bg-amber-950/15"}`}>
            <div className="flex items-center gap-2 mb-3">
                {overall ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <AlertTriangle className="w-5 h-5 text-amber-400" />}
                <h2 className={`text-lg font-semibold ${overall ? "text-emerald-200" : "text-amber-200"}`} data-testid="soak-overall-verdict">
                    {overall ? "GO — promote v1.0.25 to production" : "NO-GO — review failing gates"}
                </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {gateEntries.map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 text-xs" data-testid={`soak-gate-${k}`}>
                        {v ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                           : <XCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />}
                        <span className="text-zinc-200">{GATE_LABEL[k] || k}</span>
                    </div>
                ))}
            </div>
        </section>
    );
}

function Metric({ label, value, sub, icon, tone = "default", "data-testid": testid }) {
    const toneCls = tone === "warn" ? "bg-amber-950/20 border-amber-800/40"
                  : tone === "ok"   ? "bg-emerald-950/20 border-emerald-800/40"
                  :                   "bg-zinc-950/40 border-zinc-800";
    return (
        <div className={`rounded-md border p-3 ${toneCls}`} data-testid={testid}>
            <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
                <span className="text-zinc-500">{icon}</span>
            </div>
            <div className="mt-1 text-2xl font-semibold text-zinc-100">{value}</div>
            {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
        </div>
    );
}
